import React, { useEffect, useState } from 'react';
import {
  Box,
  Button,
  Card,
  darken,
  LinearProgress,
  Stack,
  Tab,
  Tabs,
  Typography,
} from '@mui/material';
import KeyboardArrowRightIcon from '@mui/icons-material/KeyboardArrowRight';
import { Link } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { TP_COLORS } from '../../../../constants';
import {
  getSubjectColorNew,
  getSubjectIconName,
  getUrlConvertedString,
} from '../../../../helper';
import {
  getProgressV2State,
  ISubjectForProgress,
} from '../../../../store/V2/ProgressSlice';
import { useAppSelector } from '../../../../hooks';
import {
  getTopicState,
  getTopicsWithSubTopics,
} from '../../../../store/TopicSlice';
import {
  IGroupedSubTopic,
  IGroupedTopic,
} from '../../../../store/SubTopicSlice';
import {
  checkQuizzes,
  checkResources,
} from '../../../UserPages/Subject/components/SubTopicList';

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

export const getLatestSubject = (
  subjects: ISubjectForProgress[]
): ISubjectForProgress | undefined => {
  const filteredSubjects = subjects.filter((subject) => subject.lastActivityAt);

  if (filteredSubjects.length === 0) {
    return undefined;
  }

  return filteredSubjects.reduce((latest, current) => {
    return new Date(current.lastActivityAt!) > new Date(latest.lastActivityAt!)
      ? current
      : latest;
  });
};

const getLastActivityCompletion = (subject?: ISubjectForProgress): number => {
  if (subject === undefined || subject.lastActivitySubTopic === undefined) {
    return 0;
  }
  const lastActivityId = subject?.lastActivitySubTopic?._id;

  return (
    subject.topics
      .flatMap((topic) => topic.subTopics)
      .find((subTopic) => subTopic._id === lastActivityId)?.completion ?? 0
  );
};

export const findNextSubtopic = (
  topics: IGroupedTopic[],
  currentSubtopicId: string
): IGroupedSubTopic | null => {
  let foundCurrent = false;

  for (let i = 0; i < topics.length; i += 1) {
    const topic = topics[i];
    const { subTopics } = topic;

    for (let j = 0; j < subTopics.length; j += 1) {
      const subtopic = subTopics[j];

      if (
        foundCurrent &&
        topic.isAssessment === false &&
        (checkResources(subtopic) || checkQuizzes(subtopic))
      ) {
        return subtopic;
      }

      if (subtopic._id === currentSubtopicId) {
        foundCurrent = true;
      }
    }
  }

  return null;
};

function CustomTabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`simple-tabpanel-${index}`}
      aria-labelledby={`simple-tab-${index}`}
      {...other}
    >
      {value === index && <Box>{children}</Box>}
    </div>
  );
}

function a11yProps(index: number) {
  return {
    id: `simple-tab-${index}`,
    'aria-controls': `simple-tabpanel-${index}`,
  };
}

const TabSection: React.FC = () => {
  const dispatch = useDispatch();

  // State
  const [value, setValue] = useState(0);

  // Store
  const { progress } = useAppSelector(getProgressV2State);
  const { topicsWithSubTopics } = useAppSelector(getTopicState);

  const latestSubject = getLatestSubject(progress?.subjects || []);
  const subject = latestSubject?.subject || '';
  const completionValue = getLastActivityCompletion(latestSubject);
  const subjectColor = getSubjectColorNew(subject);

  useEffect(() => {
    if (
      latestSubject &&
      progress &&
      progress.curriculumId &&
      progress.curriculumId.name
    ) {
      dispatch(
        getTopicsWithSubTopics({
          curriculum: progress.curriculumId.name,
          subject,
          subjectCode: latestSubject.subjectCode,
          subjectLevel: latestSubject.subjectLevel,
        })
      );
    }
  }, []);

  const handleChange = (event: React.SyntheticEvent, newValue: number) => {
    setValue(newValue);
  };

  const getSubTopicUrl = (): string => {
    let url = '';
    if (latestSubject?.lastActivitySubTopic) {
      const { lastActivitySubTopic } = latestSubject;

      const urlConvertedSubject = getUrlConvertedString(latestSubject.subject);
      const urlConvertedCurriculum = getUrlConvertedString(
        progress?.curriculumId?.name || ''
      );

      let subTopicId = lastActivitySubTopic._id;
      let subTopicName = lastActivitySubTopic.name;
      let topicName = lastActivitySubTopic.topic.name;
      let subTopicResources = lastActivitySubTopic.resources;

      if (completionValue > 99 && topicsWithSubTopics.length) {
        const nextSubtopic = findNextSubtopic(topicsWithSubTopics, subTopicId);

        if (nextSubtopic) {
          subTopicId = nextSubtopic._id;
          subTopicName = nextSubtopic.name;
          subTopicResources = nextSubtopic.resources;
          topicName =
            topicsWithSubTopics.find((t) => t._id === nextSubtopic.topic)
              ?.name ?? '';
        }
      }

      const urlConvertedSubTopic = getUrlConvertedString(subTopicName);
      const urlConvertedTopic = getUrlConvertedString(topicName);

      url = `${process.env.PUBLIC_URL}/dashboard/${urlConvertedCurriculum}/${urlConvertedSubject}/${urlConvertedTopic}/${urlConvertedSubTopic}/${subTopicId}`;

      if (!subTopicResources || subTopicResources.length === 0) {
        url += '/quiz';
      }
    }

    return url;
  };

  return (
    <Box sx={{ width: '100%', marginTop: 2 }}>
      <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
        <Tabs
          value={value}
          onChange={handleChange}
          aria-label="basic tabs"
          sx={{
            '& .MuiTabs-indicator': {
              backgroundColor: TP_COLORS.gray.darken,
            },
          }}
        >
          <Tab
            label="Current Topic"
            sx={{
              '&.Mui-selected': {
                color: TP_COLORS.gray.darken,
              },
            }}
            {...a11yProps(0)}
          />
          {/* <Tab label="Upcoming Classes" {...a11yProps(1)} /> */}
        </Tabs>
      </Box>

      {/* Current Topic Tab */}
      {latestSubject && (
        <CustomTabPanel value={value} index={0}>
          <Box display="flex" alignItems="center" margin="22px 0">
            <Typography fontSize="14px" color="#647488">
              {latestSubject.subject}
            </Typography>
            {latestSubject.lastActivitySubTopic && (
              <>
                <KeyboardArrowRightIcon sx={{ color: '#647488' }} />
                <Typography fontSize="14px" color="#647488">
                  {latestSubject.lastActivitySubTopic.topic.name}
                </Typography>
                <KeyboardArrowRightIcon sx={{ color: '#647488' }} />
                <Typography fontSize="14px" color={TP_COLORS.gray.darken}>
                  {latestSubject.lastActivitySubTopic.name}
                </Typography>
              </>
            )}
          </Box>

          <Card
            variant="outlined"
            sx={{
              padding: '25px',
              borderRadius: '10px',
              border: '1px solid #E2E8F0',
            }}
          >
            <Stack gap={2}>
              <Box display="flex" alignItems="flex-start" gap={2}>
                <Box
                  sx={{
                    width: '44px',
                    height: '44px',
                    borderRadius: '7px',
                    backgroundColor: `${subjectColor}33`,
                  }}
                >
                  <img
                    src={`${
                      process.env.PUBLIC_URL
                    }/images/landingPage/icon-${getSubjectIconName(
                      subject
                    )}.avif`}
                    alt="subject-img"
                    style={{ height: '44px' }}
                  />
                </Box>

                <Box flex={1}>
                  <Typography
                    fontSize="14px"
                    fontWeight="600"
                    color={TP_COLORS.gray.darken}
                  >
                    {latestSubject.lastActivitySubTopic?.name}
                  </Typography>
                  <Typography fontSize="14px" color="#94A3B8">
                    Completion {completionValue.toFixed()}%
                  </Typography>
                </Box>
                <Button
                  disableElevation
                  variant="contained"
                  component={Link}
                  to={getSubTopicUrl()}
                  sx={{
                    height: '44px',
                    borderRadius: '8px',
                    fontSize: '14px',
                    fontWeight: '600',
                    backgroundColor: subjectColor,
                    '&:hover': {
                      backgroundColor: darken(subjectColor, 0.3),
                    },
                  }}
                >
                  <img
                    src={`${process.env.PUBLIC_URL}/images/studentDashboard/play-icon.png`}
                    alt="continue"
                    style={{ marginRight: '8px' }}
                  />
                  Continue
                </Button>
              </Box>
              <LinearProgress
                variant="determinate"
                value={completionValue}
                sx={{
                  backgroundColor: TP_COLORS.border.color1,
                  '& .MuiLinearProgress-bar': {
                    backgroundColor: subjectColor,
                  },
                }}
              />
            </Stack>
          </Card>
        </CustomTabPanel>
      )}
    </Box>
  );
};

export default TabSection;
